import * as React from "react"
import { WidgetTourStep, WidgetTourStepSide, WidgetTourStepType } from "../../types"
import EmbeddedEditorFrame from "./EmbeddedEditorFrame"

interface IProps {
  step: WidgetTourStep
  anchorRect: DOMRect | null
  saving: boolean
  onChange: (step: WidgetTourStep) => void
  onSave: () => void
  onCancel: () => void
  onChangeElement: () => void
}

const SIDES: WidgetTourStepSide[] = [
  WidgetTourStepSide.TOP,
  WidgetTourStepSide.RIGHT,
  WidgetTourStepSide.BOTTOM,
  WidgetTourStepSide.LEFT,
]

const POPOVER_WIDTH = 300
const POPOVER_HEIGHT_ESTIMATE = 280

const StepEditorPopover = ({ step, anchorRect, saving, onChange, onSave, onCancel, onChangeElement }: IProps) => {
  const style: React.CSSProperties = anchorRect
    ? {
      position: "fixed",
      top: Math.min(anchorRect.bottom + 12, window.innerHeight - POPOVER_HEIGHT_ESTIMATE - 16),
      left: Math.min(Math.max(anchorRect.left, 16), window.innerWidth - POPOVER_WIDTH - 16),
    }
    : {
      position: "fixed",
      top: "35%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    }

  return (
    <div className="bizzey-tb-popover" style={style}>
      <div className="bizzey-tb-popover__header">
        <span>{step.id ? "Edit step" : "New step"}</span>
        <button type="button" className="bizzey-tb-icon-button" onClick={onCancel} aria-label="Close">×</button>
      </div>

      <input
        className="bizzey-tb-input"
        type="text"
        placeholder="Title"
        value={step.title || ""}
        onChange={(e) => onChange({ ...step, title: e.currentTarget.value })}
      />

      <EmbeddedEditorFrame
        key={step.id || "new"}
        value={step.description || ""}
        placeholder="Type your content here..."
        onChange={(description) => onChange({ ...step, description })}
      />

      {step.type !== WidgetTourStepType.POST && (
        <>
          <div className="bizzey-tb-field-row">
            <span className="bizzey-tb-field-label">Element</span>
            <input
              className="bizzey-tb-selector"
              type="text"
              value={step.element || ""}
              placeholder="CSS selector"
              onChange={(e) => onChange({ ...step, element: e.currentTarget.value })}
            />
            <button type="button" className="bizzey-tb-link-button" onClick={onChangeElement}>Change</button>
          </div>

          <div className="bizzey-tb-field-row">
            <span className="bizzey-tb-field-label">Side</span>
            <div className="bizzey-tb-side-picker">
              {SIDES.map((side) => (
                <button
                  key={side}
                  type="button"
                  className={`bizzey-tb-side-option${step.side === side ? " bizzey-tb-side-option--active" : ""}`}
                  onClick={() => onChange({ ...step, side })}
                >
                  {side}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="bizzey-tb-popover__footer">
        <button type="button" className="bizzey-tb-button bizzey-tb-button--ghost" onClick={onCancel}>Cancel</button>
        <button
          type="button"
          className="bizzey-tb-button bizzey-tb-button--primary"
          disabled={!step.title || saving}
          onClick={onSave}
        >
          {step.id ? "Update" : "Add step"}
        </button>
      </div>
    </div>
  )
}

export default StepEditorPopover
